import React from 'react';
import { ScrollAnimate } from '../components/ScrollAnimate';
import { SEO } from '../components/SEO';
import { IconGlobe, IconLightbulb, IconPeople } from '../components/icons/SiteIcons';

const regions = [
  { flag: '🇬🇭', region: 'Africa', text: 'Ghana DPA, NDPR, POPIA' },
  { flag: '🇪🇺', region: 'Europe', text: 'GDPR, UK GDPR' },
  { flag: '🇺🇸', region: 'North America', text: 'CCPA, NIST, PIPEDA' },
  { flag: '🇯🇵', region: 'Asia Pacific', text: 'PDPA, PDPB, AU Privacy Act' },
  { flag: '🇦🇪', region: 'Middle East', text: 'UAE regulations' },
  { flag: '🇧🇷', region: 'Latin America', text: 'LGPD, regional frameworks' },
];

export function AboutPage() {
  return (
    <>
      <SEO
        title="About logNsec"
        description="logNsec is a software company building products from cybersecurity to business platforms like Koklonya — with security as the bedrock. Our mission is to make enterprise-grade software and security accessible to every business."
        canonical="/about"
      />
      <section className="bg-[#F5F5F5] px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-4xl">
          <ScrollAnimate>
            <p className="text-label-site text-[#65676B]">About logNsec</p>
            <h1 className="text-section mt-3">
              We Believe Every Business
              <br />
              Deserves Real Security
            </h1>
            <p className="mt-6 text-body-site">
              logNsec was founded on a simple belief: that cybersecurity should not be a
              privilege reserved for large enterprises with big budgets. Today we&apos;re a
              software company — building both security products and the business platforms
              that run on them, like Koklonya — with security as the bedrock of everything
              we ship, never an afterthought.
            </p>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-white px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-[720px]">
          <ScrollAnimate>
            <div className="text-center">
              <span className="inline-block text-5xl font-serif leading-none text-[#0064E0]" aria-hidden>
                &ldquo;
              </span>
            </div>
            <p className="mt-4 text-center text-2xl font-normal leading-[1.7] text-[#1C1E21]">
              Our mission is to make enterprise-grade cybersecurity accessible to every
              business — from a market trader in Accra to a logistics company in London — through
              AI-powered products and expert managed services delivered remotely.
            </p>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#F5F5F5] px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimate>
            <div className="grid grid-cols-1 items-stretch gap-6 md:grid-cols-3">
              {[
                {
                  icon: <IconPeople size={32} />,
                  title: 'Accessibility',
                  body: 'Security tools that any business owner can understand and afford — regardless of technical background or budget size.',
                },
                {
                  icon: <IconLightbulb size={32} />,
                  title: 'Innovation',
                  body: 'We build with the latest AI and open-source security tooling to stay ahead of the threats our clients face every day.',
                },
                {
                  icon: <IconGlobe size={32} />,
                  title: 'Impact',
                  body: 'We measure success by the businesses we protect and the livelihoods we secure — not just the revenue we generate.',
                },
              ].map((v) => (
                <article key={v.title} className="card-site flex h-full flex-col">
                  {v.icon}
                  <h2 className="text-card-title mt-5">{v.title}</h2>
                  <p className="mt-4 text-body-site">{v.body}</p>
                </article>
              ))}
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-white px-5 py-20 md:px-8 md:py-24">
        <div className="mx-auto max-w-7xl">
          <ScrollAnimate>
            <h2 className="text-section">Built for the World</h2>
            <p className="mt-6 max-w-3xl text-body-site">
              logNsec products and services adapt to local languages, currencies, compliance
              frameworks, and threat landscapes.
            </p>
          </ScrollAnimate>
          <ScrollAnimate className="mt-12">
            <div className="grid grid-cols-1 items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {regions.map((r) => (
                <article key={r.region} className="card-site flex h-full flex-col text-center">
                  <span className="text-3xl" aria-hidden>
                    {r.flag}
                  </span>
                  <h3 className="text-card-title mt-3">{r.region}</h3>
                  <p className="mt-2 text-body-site">{r.text}</p>
                </article>
              ))}
            </div>
          </ScrollAnimate>
        </div>
      </section>

      <section className="bg-[#0064E0] px-5 py-16 md:px-8 md:py-20">
        <ScrollAnimate>
          <div className="mx-auto grid max-w-7xl grid-cols-1 gap-10 text-center sm:grid-cols-2 lg:grid-cols-4 lg:gap-6">
            {[
              ['Founded', '2025'],
              ['Products', '1 Live + 3 In Development'],
              ['Markets', 'Global'],
              ['Model', 'Remote-First'],
            ].map(([k, v]) => (
              <div key={k}>
                <p className="text-[15px] text-white/80">{k}</p>
                <p className="mt-2 text-xl font-semibold text-white md:text-2xl">{v}</p>
              </div>
            ))}
          </div>
        </ScrollAnimate>
      </section>
    </>
  );
}
